import { useState, useContext } from 'react';
import { Button, Modal, ModalBody } from 'reactstrap';
import toast from 'react-hot-toast';
import { updateFundRequest } from '~/services/fundrequests';
import { AdminContext } from '~/context/admin';
import { FundRequestContext } from '~/context/fundrequests';

interface PartialDisbursementModalProps {
    isOpen: boolean;
    toggle: () => void;
    fundRequest: any;
}

export const PartialDisbursementModal = ({
    isOpen,
    toggle,
    fundRequest,
}: PartialDisbursementModalProps) => {
    const [submitting, setSubmitting] = useState(false);
    const [{ campaignId }] = useContext(AdminContext);
    const [, fundRequestDispatch] = useContext(FundRequestContext);

    // Get the partial disbursement message from the dedicated field
    const getPartialDisbursementMessage = () => {
        // Use the dedicated partial_disbursement_note field
        if (fundRequest?.partial_disbursement_note?.note) {
            return fundRequest.partial_disbursement_note.note;
        }
        
        // Fallback to searching in notes for backward compatibility
        const statusNote = fundRequest?.notes?.find(note => 
            note.fund_request_status?.value === 'needs_info' ||
            (note.fund_request_status?.value === fundRequest.fund_request_status.value && !note.is_client_note)
        );
        
        if (statusNote?.note) {
            return statusNote.note;
        }
        
        // Fallback to a default message if no specific note is found
        return "There are currently not enough funds available to meet the requested amount. You may request a partial disbursement for what is available and in the future, when more funds are available, the remainder will be disbursed.";
    };

    const handleAcceptPartial = async () => {
        try {
            setSubmitting(true);
            
            // Update the fund request to accept partial disbursement
            const updatedRequest = await updateFundRequest(
                {
                    ...fundRequest,
                    partial_status: 'accepted',
                    // Keep existing data while updating partial status
                    transactions_default: fundRequest.transactions_default || [],
                },
                fundRequest.id,
                campaignId
            );

            if (updatedRequest && 'error' in updatedRequest) {
                throw new Error(String(updatedRequest.error));
            }

            // Update the fund request in context
            fundRequestDispatch({
                type: 'set_fund_request',
                payload: updatedRequest,
            });

            toast.success('Partial disbursement accepted');
            toggle();
        } catch (error) {
            console.error('Error accepting partial disbursement:', error);
            toast.error('Failed to accept partial disbursement');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeclinePartial = async () => {
        try {
            setSubmitting(true);
            
            // Update the fund request to decline partial disbursement
            const updatedRequest = await updateFundRequest(
                {
                    ...fundRequest,
                    partial_status: 'declined',
                    // Keep existing data while updating partial status
                    transactions_default: fundRequest.transactions_default || [],
                },
                fundRequest.id,
                campaignId
            );

            if (updatedRequest && 'error' in updatedRequest) {
                throw new Error(String(updatedRequest.error));
            }

            // Update the fund request in context
            fundRequestDispatch({
                type: 'set_fund_request',
                payload: updatedRequest,
            });

            toast.success('Partial disbursement declined');
            toggle();
        } catch (error) {
            console.error('Error declining partial disbursement:', error);
            toast.error('Failed to decline partial disbursement');
        } finally {
            setSubmitting(false);
        }
    };

    const message = getPartialDisbursementMessage();

    return (
        <Modal centered isOpen={isOpen} toggle={toggle} size="lg">
            <ModalBody>
                <h3 className="text-modal modal__title">
                    There are insufficient funds
                </h3>
                
                <div style={{ marginBottom: '24px', whiteSpace: 'pre-line' }}>
                    {message}
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <strong>
                        Note: If you decline to accept the partial coverage, your request will be rejected.
                    </strong>
                </div>

                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '12px', 
                    width: '100%',
                }}>
                    <Button
                        color="outline-dark"
                        onClick={handleDeclinePartial}
                        disabled={submitting}
                        style={{
                            borderRadius: '24px',
                            padding: '12px 24px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            width: '100%',
                        }}
                    >
                        DECLINE PARTIAL
                    </Button>
                    <Button
                        color="warning"
                        onClick={handleAcceptPartial}
                        disabled={submitting}
                        style={{
                            backgroundColor: '#F59E0B',
                            borderColor: '#F59E0B',
                            borderRadius: '24px',
                            padding: '12px 24px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            width: '100%',
                        }}
                    >
                        {submitting ? 'PROCESSING...' : 'ACCEPT PARTIAL'}
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    );
}; 